import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  AdminRelatedLinks,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminApplicantDetail,
  sendYowanAdminApplicantMemberRegistration,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const readOnlyGridStyle = {
  display: 'grid',
  gap: '0.85rem',
};

const actionButtonStyle = {
  appearance: 'none',
  border: '1px solid rgba(89, 102, 116, 0.22)',
  borderRadius: '999px',
  background: 'rgba(89, 102, 116, 0.1)',
  color: '#24303a',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '0.86rem',
  fontWeight: 700,
  lineHeight: 1.2,
  minHeight: '2.2rem',
  padding: '0.45rem 0.9rem',
};

const unavailableMessageStyle = {
  alignItems: 'center',
  color: '#596674',
  display: 'flex',
  fontSize: '0.78rem',
  gap: '0.42rem',
  lineHeight: 1.55,
  margin: 0,
};

const unavailableMessageIconStyle = {
  alignItems: 'center',
  border: '1px solid rgba(89, 102, 116, 0.36)',
  borderRadius: '50%',
  color: '#596674',
  display: 'inline-flex',
  flex: '0 0 auto',
  fontSize: '0.66rem',
  fontWeight: 700,
  height: '1rem',
  justifyContent: 'center',
  lineHeight: 1,
  width: '1rem',
};

const ReadOnlyField = ({ label, value, multiline = false, rows = 4 }) => {
  const fieldValue = value || '-';

  return (
    <label className="c-form__field yowan-admin-readonly-field" style={{ cursor: 'not-allowed' }}>
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea
          className="c-form__control c-form__control--textarea"
          readOnly
          rows={rows}
          style={{ cursor: 'not-allowed' }}
          value={fieldValue}
        />
      ) : (
        <input
          className="c-form__control"
          readOnly
          style={{ cursor: 'not-allowed' }}
          type="text"
          value={fieldValue}
        />
      )}
    </label>
  );
};

const ApplicantIdentityObservationList = ({ observations = [], preservedDomainSearch }) => {
  if (!observations.length) {
    return null;
  }

  return (
    <section className="yowan-admin-form c-surface-content yowan-applicant-identity-observations" style={readOnlyGridStyle}>
      <AdminPageHeader
        title="同一メールアドレスの観測履歴"
        paragraphs={[
          '同一メールアドレスで別名・別ふりがな・別電話番号などが観測された申込を表示します。各申込時点の内容は教室申込詳細で確認できます。',
        ]}
      />
      <div className="yowan-applicant-identity-observations__list">
        {observations.map((observation, index) => (
          <article className="yowan-applicant-identity-observations__card" key={observation.id || index}>
            <header className="yowan-applicant-identity-observations__header">
              <div className="yowan-applicant-identity-observations__summary">
                <p className="yowan-applicant-identity-observations__title">
                  {observation.observedName || observation.observedEmail || `観測履歴 ${index + 1}`}
                </p>
                <p className="yowan-applicant-identity-observations__meta">
                  {[
                    observation.observedAt,
                    observation.sourceLabel,
                    observation.changedFieldsLabel ? `差分: ${observation.changedFieldsLabel}` : '',
                  ].filter(Boolean).join(' / ')}
                </p>
              </div>
              {observation.classApplicationHref ? (
                <Link
                  className="yowan-applicant-identity-observations__link"
                  to={appendYowanPreviewQueryIfNeeded(observation.classApplicationHref, preservedDomainSearch)}
                >
                  教室申込詳細へ
                </Link>
              ) : null}
            </header>
            <dl className="yowan-applicant-identity-observations__grid">
              <div>
                <dt>ふりがな</dt>
                <dd>{observation.observedNameKana || '-'}</dd>
              </div>
              <div>
                <dt>メールアドレス</dt>
                <dd className="yowan-applicant-identity-observations__email">
                  {observation.observedEmail || '-'}
                </dd>
              </div>
              <div>
                <dt>電話番号</dt>
                <dd>{observation.observedPhone || '-'}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
};

const applicantManagementStatusLabel = (isActive) => (isActive ? '管理中' : '停止中');

const applicantInvitationStatusLabel = (record) => {
  const request = record.latestOnboardingRequest;

  if (request?.statusLabel) {
    return request.statusLabel;
  }

  return record.onboardingInvitationStatusLabel && record.onboardingInvitationStatusLabel !== '-'
    ? record.onboardingInvitationStatusLabel
    : '未送信';
};

const applicantMemberRegistrationStatusLabel = (record) => {
  if (record.memberRegistration?.canSend) {
    return record.memberRegistration?.reasonLabel || '送信できます';
  }

  return record.memberRegistration?.reasonLabel
    || record.memberRegistration?.detail
    || '送信できません';
};

const applicantMemberRegistrationUnavailableMessage = (record, fallbackMessage) => {
  const message = record.memberRegistration?.reasonLabel
    || record.memberRegistration?.detail
    || fallbackMessage;
  const expiresAt = record.memberRegistration?.latestRequestExpiresAt;

  if (record.memberRegistration?.reason === 'pending_member_registration_request' && expiresAt) {
    return `${message} 有効期限: ${expiresAt}`;
  }

  return message;
};

const contactMessageLabel = (record, content) => {
  if (record.applicantType === 'application') {
    return '申込メッセージ';
  }

  return content.labels.message;
};

const contactMessageValue = (record) => {
  const sourceMessage = record.sourceDetail?.message || '';
  const message = sourceMessage || record.note || '';

  if (message) {
    return message;
  }

  if (record.applicantType === 'application') {
    return '申込メッセージは申込詳細で確認してください。';
  }

  return '-';
};

const resolveMemberRegistrationErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.detail
  || error?.response?.data?.member_registration?.detail
  || fallbackMessage;

const AdminApplicantDetailPage = () => {
  const content = yowanPageContent['/admin/applicants/:applicantId'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { applicantId } = useParams();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);

  useEffect(() => {
    if (!content || !applicantId) {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanAdminApplicantDetail(applicantId)
      .then((payload) => {
        if (isMounted) {
          setRecord(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setLoadError(content.loadErrorMessage);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [applicantId, content]);

  if (!content) {
    return null;
  }

  const backToApplicants = appendYowanPreviewQueryIfNeeded('/admin/applicants', preservedDomainSearch);
  const canSendRegistration = record?.memberRegistration?.canSend === true;
  const handleSendInvitation = async () => {
    if (!canSendRegistration) {
      return;
    }

    if (!window.confirm('この問い合わせ・申込者へ会員登録フォームを送信しますか？')) {
      return;
    }

    setIsSendingInvitation(true);
    setActionMessage(content.actions.sendingMessage);

    try {
      const nextRecord = await sendYowanAdminApplicantMemberRegistration(record.id);
      setRecord(nextRecord);
      setActionMessage(content.actions.sentMessage);
    } catch (error) {
      setActionMessage(resolveMemberRegistrationErrorMessage(error, content.actions.sendErrorMessage));
    } finally {
      setIsSendingInvitation(false);
    }
  };

  if (!record) {
    return (
      <AdminBlogShell activeNavHref="/admin/applicants" routeKey="/admin/applicants/:applicantId">
        <AdminPageHeader
          title={content.header.title}
          paragraphs={isLoading ? [] : [loadError || content.notFoundMessage]}
          actions={[
            <Link key="back-to-applicants" to={backToApplicants}>
              一覧へ戻る
            </Link>,
          ]}
        />
        {isLoading ? <LoadingSpinner /> : null}
      </AdminBlogShell>
    );
  }

  return (
    <AdminBlogShell activeNavHref="/admin/applicants" routeKey="/admin/applicants/:applicantId">
      <AdminPageHeader
        title={`${record.name || '氏名未設定'} / ${record.applicantTypeLabel}`}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-applicants" to={backToApplicants}>
            一覧へ戻る
          </Link>,
          record.memberId ? (
            <Link
              key="member-detail"
              to={appendYowanPreviewQueryIfNeeded(`/admin/members/${record.memberId}/edit`, preservedDomainSearch)}
            >
              会員を確認
            </Link>
          ) : null,
          record.sourceDetailHref ? (
            <Link
              key="source-detail"
              to={appendYowanPreviewQueryIfNeeded(record.sourceDetailHref, preservedDomainSearch)}
            >
              正本詳細を確認
            </Link>
          ) : null,
        ]}
      />
      <AdminRelatedLinks
        links={content.relatedLinks}
        preservedDomainSearch={preservedDomainSearch}
      />

      <section className="yowan-admin-form c-surface-content" style={readOnlyGridStyle}>
        <ReadOnlyField label={content.labels.id} value={record.id} />
        <ReadOnlyField label={content.labels.name} value={record.name} />
        <ReadOnlyField label={content.labels.nameKana} value={record.nameKana} />
        <ReadOnlyField label={content.labels.email} value={record.email} />
        <ReadOnlyField label={content.labels.phone} value={record.phone} />
        <ReadOnlyField label={content.labels.type} value={record.applicantTypeLabel} />
        <ReadOnlyField label={content.labels.source} value={record.sourceDetailLabel} />
        <ReadOnlyField label={content.labels.sourceKind} value={record.sourceKindLabel} />
        <ReadOnlyField label={content.labels.memberLink} value={record.memberId ? '会員化済み' : '未連携'} />
        <ReadOnlyField label={content.labels.managementStatus} value={applicantManagementStatusLabel(record.isActive)} />
        <ReadOnlyField label={content.labels.invitationStatus} value={applicantInvitationStatusLabel(record)} />
        <ReadOnlyField
          label={content.labels.memberRegistration}
          value={applicantMemberRegistrationStatusLabel(record)}
        />
        <ReadOnlyField label={content.labels.invitationSentAt} value={record.onboardingInvitationSentAt} />
        <ReadOnlyField label={content.labels.createdAt} value={record.createdAt} />
        <ReadOnlyField label={content.labels.updatedAt} value={record.updatedAt} />
        <ReadOnlyField
          label={contactMessageLabel(record, content)}
          value={contactMessageValue(record)}
          multiline
          rows={8}
        />
      </section>

      <ApplicantIdentityObservationList
        observations={record.identityObservations || []}
        preservedDomainSearch={preservedDomainSearch}
      />

      <section className="yowan-admin-form c-surface-content" style={readOnlyGridStyle}>
        <AdminPageHeader
          title={content.actions.title}
          paragraphs={content.actions.paragraphs}
        />
        {canSendRegistration ? (
          <div className="yowan-admin-form__actions">
            <button
              type="button"
              style={{
                ...actionButtonStyle,
                cursor: isSendingInvitation ? 'not-allowed' : 'pointer',
                opacity: isSendingInvitation ? 0.56 : 1,
              }}
              disabled={isSendingInvitation}
              onClick={handleSendInvitation}
            >
              {isSendingInvitation ? content.actions.sendingLabel : content.actions.sendInvitationLabel}
            </button>
          </div>
        ) : (
          <>
            <div className="yowan-admin-form__actions">
              <button
                type="button"
                style={{
                  ...actionButtonStyle,
                  cursor: 'not-allowed',
                  opacity: 0.56,
                }}
                disabled
              >
                {content.actions.sendInvitationLabel}
              </button>
            </div>
            <p className="yowan-admin-form__message" style={unavailableMessageStyle}>
              <span aria-hidden="true" style={unavailableMessageIconStyle}>!</span>
              <span>{applicantMemberRegistrationUnavailableMessage(record, content.actions.unavailableMessage)}</span>
            </p>
          </>
        )}
        {actionMessage ? <p className="yowan-admin-form__message">{actionMessage}</p> : null}
      </section>
    </AdminBlogShell>
  );
};

export default AdminApplicantDetailPage;
